/**
 * MapBackgroundFields — the galaxy map's translucent backdrop, as a controlled
 * field set for the Map Settings modal's Background tab.
 *
 * Lets the admin pick a background image from the shared-assets registry (or
 * "None"), a tint colour, and an opacity. The backdrop is a recolourable
 * vector: the chosen SVG is used as a CSS mask and the tint colour paints
 * through its alpha, so the same file can render in any colour (matching the
 * render path in _trl-map-builder.scss).
 *
 * A live preview mirrors that render over a dark galaxy swatch so the admin
 * sees the exact tint + opacity before saving. `undefined` means "no backdrop"
 * — picking "None" reports it back that way.
 */

import {
  DEFAULT_TRL_MAP_BACKGROUND,
  type TrlMapBackground,
} from '@swtorbit/shared-socket-events';
import { TRL_MAP_BACKGROUNDS, getMapBackgroundUrl } from '@swtorbit/shared-assets';

interface MapBackgroundFieldsProps {
  /** The map's current backdrop, or undefined for no backdrop. */
  value?: TrlMapBackground;
  onChange: (next: TrlMapBackground | undefined) => void;
}

const NONE = '';

export function MapBackgroundFields({ value, onChange }: MapBackgroundFieldsProps) {
  const image = value?.image ?? NONE;
  const color = value?.color ?? DEFAULT_TRL_MAP_BACKGROUND.color;
  const opacity = value?.opacity ?? DEFAULT_TRL_MAP_BACKGROUND.opacity;

  const maskUrl = image ? getMapBackgroundUrl(image) : '';

  /** "None" clears the backdrop entirely; otherwise carry the resolved trio. */
  const patch = (next: Partial<TrlMapBackground>) => {
    const merged = { image, color, opacity, ...next };
    onChange(merged.image ? merged : undefined);
  };

  return (
    <div className="d-flex flex-column gap-sm">
      {/* Live preview over a dark galaxy swatch */}
      <div
        className="rounded-xs"
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          background: 'var(--color-space)',
          overflow: 'hidden',
          border: '1px solid color-mix(in srgb, var(--color-cyan-light) 35%, transparent)',
        }}
      >
        {maskUrl && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: color,
              opacity,
              WebkitMaskImage: `url("${maskUrl}")`,
              maskImage: `url("${maskUrl}")`,
              WebkitMaskSize: 'cover',
              maskSize: 'cover',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskPosition: 'center',
            }}
          />
        )}
        {!maskUrl && (
          <span
            className="text-slate fs-xs"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            No background
          </span>
        )}
      </div>

      {/* Image picker (None + registry entries) */}
      <label className="d-flex flex-column gap-2xs fs-sm">
        <span>Background image</span>
        <select
          className="input"
          value={image}
          onChange={(e) => patch({ image: e.target.value })}
        >
          <option value={NONE}>None</option>
          {TRL_MAP_BACKGROUNDS.map((bg) => (
            <option key={bg.id} value={bg.id}>
              {bg.label}
            </option>
          ))}
        </select>
      </label>

      {/* Tint colour (disabled when None) */}
      <label className="d-flex align-items-center gap-xs fs-sm">
        <span>Colour</span>
        <input
          type="color"
          className="input"
          style={{ width: '4rem', padding: 0 }}
          value={toHexColor(color)}
          disabled={!image}
          onChange={(e) => patch({ color: e.target.value })}
        />
      </label>

      {/* Opacity (disabled when None) */}
      <label className="d-flex align-items-center gap-xs fs-sm">
        <span>Opacity</span>
        <input
          className="range"
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={opacity}
          disabled={!image}
          onChange={(e) => patch({ opacity: Number(e.target.value) })}
          style={{ flex: 1 }}
        />
        <span className="fs-xs text-slate" style={{ width: '3rem', textAlign: 'right' }}>
          {Math.round(opacity * 100)}%
        </span>
      </label>
    </div>
  );
}

/**
 * `<input type="color">` only accepts `#rrggbb`. Authored colours may be other
 * CSS forms — fall back to a sensible hex so the swatch stays usable; picking a
 * new value overwrites with a clean hex.
 */
function toHexColor(color: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#71d9ff';
}
